import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { NgClass, JsonPipe } from '@angular/common';
import { Router } from "@angular/router";
import { CmProAuthService } from '@app/core/services/cmpro-auth.service';
import { UseCPLoader, ICPLoader } from '@app/cp/shared/decorators/cploader/cploader-decorator';
import { UsersBus } from '@app/cp/shared/models/UsersBus.model';
import { AuthenticationService } from '@app/cp/shared/services/authentication.service';
import { GlobalsService } from '@app/cp/shared/services/globals.service';
import { environment } from '@env/environment';
import { AppClaims } from "@app/cp/shared/models/AppClaims";
import { CommonFunctions } from "@app/cp/shared/static/common-functions";
import { LogoutComponent } from "@app/core/user/logout/logout.component";
import { SharedComponentsModule } from '@app/cp/shared/sharedComponents.module';
import { CommonValues } from '@app/cp/shared/static/CommonValues';
import { DQOIReferenceDataQuery } from '@app/cp/dqoi/state/dqoi-reference-data.query';
import { DispatchesService } from '@app/cp/shared/services/dispatches.service';
import { nameOf } from '@app/core/utils/type-helpers';
import { NavStateService } from "@app/core/layout/navigation/nav-state.service";
import { KENDO_BUTTON } from "@progress/kendo-angular-buttons";

@UseCPLoader()
@Component({
    selector: 'cp-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: true,
    imports: [ NgClass, JsonPipe, SharedComponentsModule, KENDO_BUTTON, LogoutComponent ],
})
export class HeaderComponent implements OnInit, OnDestroy {

    private _router = inject(Router);
    private _authService = inject(CmProAuthService);
    private _authenticationService = inject(AuthenticationService);
    private _globals = inject(GlobalsService);
    private _elementRef = inject(ElementRef);
    private _qry_rd = inject(DQOIReferenceDataQuery);
    private _dispatchService = inject(DispatchesService);
    private _navState = inject(NavStateService);

    @ViewChild(LogoutComponent) logoutComponent: LogoutComponent;

    compLoader: ICPLoader = { cpLoader: false, cpLoaderModal: false, cpLoaderSize: 'sm', message: '', fullScreen: false, customCss: '',
        cpLoaderOn(modal = false, size = 'sm') { this.cpLoaderSize = size; this.cpLoaderModal = modal; this.cpLoader = true; },
        cpLoaderOff() { this.cpLoader = false; this.cpLoaderModal = false; }
    };

    envReleaseVers = environment.releaseVersion;
    public userName: string;
    public permissions: AppClaims;
    public loggedInUser: boolean = false;
    public showProfileCard: boolean = false;
    public toggledata: boolean = false;
    private fullScreenToggle: boolean = false;
    protected fullScreenButtonText: string = 'Fullscreen';
    private observer!: MutationObserver;
    private user: UsersBus;
    showAccessLoader: boolean;
    showLocalLoader: boolean;
    showServerLoader: boolean;

    ngOnInit() {
        this._authService.subscribeToLoginEvents((user: UsersBus) => {
            if (user) {
                this.user = user;
                this.userName = user.username;
                this.permissions = user.appClaims;
                this.loggedInUser = true;
            } else {
                this.userName = null;
                this.loggedInUser = false;
            }
        });

    }

    ngOnDestroy() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = undefined as any;
        }
        this._authService.unsubscribeToLoginEvents();
    }

    hasServerRefDataRefreshRole() {
        return this.user.hasRole(nameOf<AppClaims>().serverReferenceDataRefresh);
    }

    refreshLocalReferenceData() {
        this.compLoader.cpLoader = true;
        this.showLocalLoader = true;
        this._qry_rd.ensureRefDataIsLoaded(this.user.companyId, true).subscribe({
            next: () => {
                this.compLoader.cpLoader = false;
                this.showLocalLoader = false;
                this._globals.notifySuccess("Local reference data refreshed");
            },
            error: (err) => {
                console.error(err);
                this.compLoader.cpLoader = false;
                this.showLocalLoader = false;
                this._globals.notifyError(err.message, true);
            }
        });
    }

    refreshServerReferenceData() {
        this.compLoader.cpLoader = true;
        this.showServerLoader = true;
        this._dispatchService.GetReferenceData(this.user.companyId, true).subscribe({
            next: () => {
                this.compLoader.cpLoader = false;
                this.showServerLoader = false;
                this._globals.notifySuccess("Server reference data refreshed");
            },
            error: (err) => {
                console.error(err);
                this.compLoader.cpLoader = false;
                this.showServerLoader = false;
                this._globals.notifyError(err.message, true);
            }
        });
    }

    showCard() {
        this.showProfileCard = !this.showProfileCard;
        this.toggledata = false;
    }

    refreshProfile() {
        this.compLoader.cpLoader = true;
        this.showAccessLoader = true;
        this._authenticationService.refreshProfile(this._authService.accountInfo.username).subscribe({
            next: (user: UsersBus) => {
                const usr = new UsersBus(user);
                if (usr.errorMessage === "User Not Found") {
                    //NOTE: dont let anything render till we have the user info
                    this._authService.logout(undefined, false).subscribe(() => {
                        this._globals.notifyError(CommonValues.ErrorMessages_AccessDenied, true);
                        this._router.navigate(['/login']);
                    });
                } else {
                    this._authService.parseUserResponse(usr, true, false);
                    this._globals.notifySuccess("User access permissions refreshed successfully.");
                    this.compLoader.cpLoader = false;
                    this.showAccessLoader = false;
                }
            },
            error: (e) => {
                console.error(e);
                this.compLoader.cpLoader = false;
                this._globals.notifyError("Unable to refresh user access. Please try again. If problem persists please contact your administrator for access.", true);
                this.showAccessLoader = false;
            }
        });
    }

    protected readonly environment = environment;

    showPermissions() {
        this.toggledata = !this.toggledata;
        this.copyPermissions();
    }

    copyPermissions() {
        CommonFunctions.copyToClipboard(JSON.stringify(this.permissions));
        this._globals.notifySuccess(`Copied Permissions for to Clipboard`);
    }

    logout() {
        this.logoutComponent.showPopup();
    }


    @HostListener('document:click', ['$event'])
    onClickOutside(event: Event) {
        if (!this._elementRef.nativeElement.contains(event.target)) {
            this.showProfileCard = false;
        }
    }

    toggleMenu(): void {
        this._navState.toggle();
    }

    toggleFullScreen(): void {
        const doc = document as any;
        const el = document.documentElement as any;

        if (!doc.fullscreenElement && !doc.webkitFullscreenElement && !doc.mozFullScreenElement) {
            const enter = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
            if (enter) enter.call(el);
            document.body.classList.add('full-screen');
        } else {
            const exit = doc.exitFullscreen || doc.webkitExitFullscreen || doc.mozCancelFullScreen || doc.msExitFullscreen;
            if (exit) exit.call(doc);
            document.body.classList.remove('full-screen');
        }
    }

}
